import { UserProfileApiGateway } from "@modules/auth/services/userProfileApi.gateway"
import { UserProfileDraftDto, UserProfileDto } from "@modules/auth/userProfileDto"
import { ApiClient } from "@modules/core/api/client/apiClient"

/**
 * SharedUserApi class
 *
 * This class provides methods for interacting with user-related API endpoints.
 * It handles operations such as retrieving, registering, and updating current user profiles,
 * as well as checking username availability.
 *
 * @class
 */
export class UserProfileApi implements UserProfileApiGateway {
  baseUrl = "/users"

  constructor(protected readonly api: ApiClient) {}

  /**
   * Retrieves the current user's profile
   * @returns A Promise that resolves to a UserProfile object
   */
  async getUser(): Promise<UserProfileDto> {
    const result = await this.api.get<UserProfileDto>(`${this.baseUrl}/me?createOrUpdate=false`)
    return result.data
  }

  /**
   * Registers a new user
   * @param draft - The UserProfileDraft object containing the new user's information
   * @returns A Promise that resolves to the created UserProfile object
   */
  async register(draft: Partial<UserProfileDraftDto>): Promise<UserProfileDto> {
    const result = await this.api.post<UserProfileDto>(`${this.baseUrl}`, draft)
    return result.data
  }

  /**
   * Updates the current user's profile
   * @param user - A partial UserProfileDraft object containing the fields to update
   * @returns A Promise that resolves to the updated UserProfile object
   */
  async update(user: Partial<UserProfileDraftDto>): Promise<UserProfileDto> {
    const result = await this.api.patch<UserProfileDto>(`${this.baseUrl}/me`, user)
    return result.data
  }

  /**
   * Checks if a username is available
   * @param username - The username to check
   * @returns A Promise that resolves to a boolean indicating whether the username is available
   */
  async checkUsername(username: string) {
    const result = await this.api.get<{ availableUsername: boolean }>(
      `${this.baseUrl}/check-username?username=${username}`,
    )
    return result.data.availableUsername
  }
}
